"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface SubProductLink {
  title: Record<Lang, string>;
  href: string;
}
interface SpecItem {
  label: Record<Lang, string>;
  value: string;
  unit: Record<Lang, string>;
}
interface ProductCategoryItem {
  id: string;
  index: string;
  title: Record<Lang, string>;
  description: Record<Lang, string>;
  specs: SpecItem[];
  subProducts: SubProductLink[];
  href: string;
  linkText: Record<Lang, string>;
  image: string;
  alt: Record<Lang, string>;
}
const categoriesData: ProductCategoryItem[] = [{
  id: "woven-wire-mesh",
  index: "01",
  title: {
    en: "Woven Wire Mesh",
    zh: "编织金属丝网",
    es: "Malla de alambre tejida",
    de: "Drahtgewebe",
    fr: "Toile métallique tissée",
    pt: "Malha de arame tecida"
  },
  description: {
    en: "Precision woven wire mesh for filtration, screening, and solid-liquid separation in square and Dutch weaves.",
    zh: "方孔与密纹精密编织金属丝网，用于工业过滤、颗粒筛分与固液分离。",
    es: "Malla metálica tejida de precisión para filtración, cribado y separación sólido-líquido en ligamentos cuadrados y holandeses.",
    de: "Präzisionsdrahtgewebe für Filtration, Siebung und Fest-Flüssig-Trennung in Quadrat- und Tressengeweben.",
    fr: "Toile métallique tissée de précision pour la filtration, le criblage et la séparation solide-liquide en tissages carrés et reps.",
    pt: "Malha metálica tecida de precisão para filtração, peneiramento e separação sólido-líquido em teceduras quadradas e holandesas."
  },
  specs: [{
    label: {
      en: "Square weave",
      zh: "方孔网",
      es: "Malla cuadrada",
      de: "Quadratgewebe",
      fr: "Tissage carré",
      pt: "Tecedura quadrada"
    },
    value: "1–635",
    unit: {
      en: "mesh",
      zh: "目",
      es: "mesh",
      de: "Mesh",
      fr: "mesh",
      pt: "mesh"
    }
  }, {
    label: {
      en: "Wire dia.",
      zh: "线径",
      es: "Diám. hilo",
      de: "Draht-Ø",
      fr: "Diam. fil",
      pt: "Diâm. fio"
    },
    value: "0.020–2.0",
    unit: {
      en: "mm",
      zh: "mm",
      es: "mm",
      de: "mm",
      fr: "mm",
      pt: "mm"
    }
  }, {
    label: {
      en: "Combined range",
      zh: "综合范围",
      es: "Rango combinado",
      de: "Kombiniert",
      fr: "Plage combinée",
      pt: "Faixa combinada"
    },
    value: "1 µm–25",
    unit: {
      en: "mm",
      zh: "mm",
      es: "mm",
      de: "mm",
      fr: "mm",
      pt: "mm"
    }
  }],
  subProducts: [{
    title: {
      en: "Square Mesh",
      zh: "方孔网",
      es: "Malla cuadrada",
      de: "Quadratgewebe",
      fr: "Toile à mailles carrées",
      pt: "Malha quadrada"
    },
    href: "/SquareMeshPage"
  }, {
    title: {
      en: "Filter Cloth (Dutch Weaves)",
      zh: "密纹过滤网",
      es: "Tela filtrante (ligamento holandés)",
      de: "Filtertressengewebe",
      fr: "Toile reps filtrante",
      pt: "Tecido filtrante (trama holandesa)"
    },
    href: "/FilterClothPage"
  }, {
    title: {
      en: "Specialty Woven Mesh",
      zh: "特种编织丝网",
      es: "Malla tejida especial",
      de: "Spezialdrahtgewebe",
      fr: "Toile tissée spéciale",
      pt: "Malha tecida especial"
    },
    href: "/SpecialtyWovenMeshPage"
  }, {
    title: {
      en: "Custom Woven Mesh",
      zh: "定制编织网",
      es: "Malla tejida a medida",
      de: "Kundenspezifisches Drahtgewebe",
      fr: "Toile tissée sur mesure",
      pt: "Malha tecida sob medida"
    },
    href: "/CustomWovenMeshPage"
  }, {
    title: {
      en: "Heavy-Duty Woven Mesh",
      zh: "重型编织网",
      es: "Malla tejida para servicio pesado",
      de: "Schweres Industriegewebe",
      fr: "Toile tissée pour usage intensif",
      pt: "Malha tecida para serviço pesado"
    },
    href: "/HeavyDutyWovenMeshPage"
  }],
  href: "/WovenWireMeshPage",
  linkText: {
    en: "Explore Products",
    zh: "了解产品",
    es: "Explorar productos",
    de: "Produkte erkunden",
    fr: "Explorer les produits",
    pt: "Explorar produtos"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-cloth-1.webp",
  alt: {
    en: "Precision woven stainless steel wire mesh closeup with square apertures",
    zh: "精密不锈钢编织丝网特写，具有方孔开口",
    es: "Primer plano de malla de alambre de acero inoxidable tejida de precisión con aberturas cuadradas",
    de: "Nahaufnahme von Präzisions-Edelstahldrahtgewebe mit quadratischen Maschenweiten",
    fr: "Gros plan sur une toile métallique tissée en acier inoxydable de précision avec ouvertures carrées",
    pt: "Primeiro plano de malha de arame tecida de aço inoxidável de precisão com aberturas quadradas"
  }
}, {
  id: "sintered-wire-mesh",
  index: "02",
  title: {
    en: "Sintered Wire Mesh",
    zh: "烧结金属网",
    es: "Malla de alambre sinterizada",
    de: "Sinterdrahtgewebe",
    fr: "Toile métallique frittée",
    pt: "Malha de arame sinterizada"
  },
  description: {
    en: "Diffusion-bonded multilayer wire mesh laminates for high differential pressures and demanding backwash cycles.",
    zh: "真空扩散结合多层金属烧结网板，专为高压差与苛刻反洗工况设计。",
    es: "Laminados multicapa unidos por difusión para altas presiones diferenciales y ciclos exigentes de retrolavado.",
    de: "Diffusionsgebundene mehrlagige Drahtgewebelaminate für hohe Differenzdrücke und anspruchsvolle Rückspülzyklen.",
    fr: "Stratifiés multicouches liés par diffusion pour pressions différentielles élevées et cycles de rétro-lavage exigeants.",
    pt: "Laminados multicamadas unidos por difusão para altas pressões diferenciais e ciclos exigentes de retrolavagem."
  },
  specs: [{
    label: {
      en: "Filtration rating",
      zh: "过滤精度",
      es: "Grado de filtración",
      de: "Filterfeinheit",
      fr: "Seuil de filtration",
      pt: "Grau de filtração"
    },
    value: "1–200",
    unit: {
      en: "µm",
      zh: "µm",
      es: "µm",
      de: "µm",
      fr: "µm",
      pt: "µm"
    }
  }, {
    label: {
      en: "Bonding process",
      zh: "结合工艺",
      es: "Proceso de unión",
      de: "Verbundverfahren",
      fr: "Liaison",
      pt: "Processo de união"
    },
    value: "Diffusion",
    unit: {
      en: "vacuum",
      zh: "真空",
      es: "al vacío",
      de: "Vakuum",
      fr: "sous vide",
      pt: "a vácuo"
    }
  }, {
    label: {
      en: "Structure",
      zh: "层状结构",
      es: "Estructura",
      de: "Struktur",
      fr: "Structure",
      pt: "Estrutura"
    },
    value: "Multilayer",
    unit: {
      en: "laminate",
      zh: "层叠板",
      es: "laminado",
      de: "Laminat",
      fr: "stratifié",
      pt: "laminado"
    }
  }],
  subProducts: [{
    title: {
      en: "Sintered Multilayer Mesh",
      zh: "多层烧结丝网",
      es: "Malla sinterizada multicapa",
      de: "Mehrlagiges Sinterdrahtgewebe",
      fr: "Toile frittée multicouche",
      pt: "Malha sinterizada multicamadas"
    },
    href: "/SinteredMultilayerMeshPage"
  }, {
    title: {
      en: "Sintered Dutch Weave Mesh",
      zh: "密纹烧结网",
      es: "Malla sinterizada de ligamento holandés",
      de: "Gesintertes Tressengewebe",
      fr: "Toile reps frittée",
      pt: "Malha holandesa sinterizada"
    },
    href: "/SinteredDutchWeaveMeshPage"
  }, {
    title: {
      en: "3-Layer Sintered Mesh",
      zh: "3层烧结丝网",
      es: "Malla sinterizada de 3 capas",
      de: "3-lagiges Sintergewebe",
      fr: "Toile frittée 3 couches",
      pt: "Malha sinterizada de 3 camadas"
    },
    href: "/ThreeLayerSinteredMeshPage"
  }, {
    title: {
      en: "Custom Sintered Mesh",
      zh: "定制烧结网",
      es: "Malla sinterizada a medida",
      de: "Kundenspezifisches Sintergewebe",
      fr: "Toile frittée sur mesure",
      pt: "Malha sinterizada sob medida"
    },
    href: "/CustomSinteredMeshPage"
  }],
  href: "/SinteredWireMeshPage",
  linkText: {
    en: "Explore Products",
    zh: "了解产品",
    es: "Explorar productos",
    de: "Produkte erkunden",
    fr: "Explorer les produits",
    pt: "Explorar productos"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/10/sintered-wire-mesh-collapse-1-1.webp",
  alt: {
    en: "Multi-layer diffusion bonded sintered stainless steel wire mesh plate",
    zh: "多层扩散结合烧结不锈钢丝网板",
    es: "Placa de malla de alambre de acero inoxidable sinterizada unida por difusión multicapa",
    de: "Mehrlagige diffusionsgebundene Sinterdrahtgewebeplatte aus Edelstahl",
    fr: "Plaque en toile métallique frittée multicouche en acier inoxydable liée par diffusion",
    pt: "Placa de malha de arame de aço inoxidável sinterizada multicamadas unida por difusão"
  }
}, {
  id: "filter-components",
  index: "03",
  title: {
    en: "Filter Components",
    zh: "深加工过滤元件",
    es: "Componentes de filtración",
    de: "Filterkomponenten",
    fr: "Composants de filtration",
    pt: "Componentes de filtração"
  },
  description: {
    en: "Fabricated filtration elements assembled to OEM equipment drawings and dimensional tolerances.",
    zh: "按 OEM 图纸与装配公差定制加工的深加工过滤元件。",
    es: "Elementos de filtración fabricados a medida según planos técnicos OEM y tolerancias dimensionales.",
    de: "Konfektionierte Filterelemente nach OEM-Zeichnungen und vorgegebenen Maßtoleranzen.",
    fr: "Éléments filtrants fabriqués sur mesure selon les plans OEM et les tolérances requises.",
    pt: "Elementos filtrantes fabricados sob encomenda conforme desenhos técnicos OEM e tolerâncias dimensionais."
  },
  specs: [{
    label: {
      en: "Discs & packs",
      zh: "滤片与滤网组",
      es: "Discos y paquetes",
      de: "Ronden & Pakete",
      fr: "Disques et packs",
      pt: "Discos e pacotes"
    },
    value: "Single / Multi",
    unit: {
      en: "layer",
      zh: "层",
      es: "capa",
      de: "lagig",
      fr: "couche",
      pt: "camada"
    }
  }, {
    label: {
      en: "Cylindrical forms",
      zh: "圆筒形态",
      es: "Formas cilíndricas",
      de: "Zylindrische Formen",
      fr: "Formes cylindriques",
      pt: "Formas cilíndricas"
    },
    value: "Tubes & Cartridges",
    unit: {
      en: "welded",
      zh: "焊接",
      es: "soldado",
      de: "geschweißt",
      fr: "soudé",
      pt: "soldado"
    }
  }, {
    label: {
      en: "Custom fabrication",
      zh: "定制形式",
      es: "Fabricación a medida",
      de: "Sonderanfertigung",
      fr: "Fabrication sur mesure",
      pt: "Fabricação sob medida"
    },
    value: "Baskets & Plates",
    unit: {
      en: "OEM drawings",
      zh: "OEM 图纸",
      es: "planos OEM",
      de: "OEM-Zeichnung",
      fr: "plans OEM",
      pt: "desenhos OEM"
    }
  }],
  subProducts: [{
    title: {
      en: "Filter Discs",
      zh: "滤圆片",
      es: "Discos filtrantes",
      de: "Filterscheiben (Ronden)",
      fr: "Disques filtrants",
      pt: "Discos filtrantes"
    },
    href: "/SlitCoilsAndStampedFilterDiscsPage"
  }, {
    title: {
      en: "Filter Tubes & Cylinders",
      zh: "滤筒与滤管",
      es: "Tubos y cilindros filtrantes",
      de: "Filterrohre & Filterzylinder",
      fr: "Tubes et cylindres filtrants",
      pt: "Tubos e cilindros filtrantes"
    },
    href: "/FilterTubesAndCylindersPage"
  }, {
    title: {
      en: "Filter Cartridges",
      zh: "滤芯组件",
      es: "Cartuchos de filtración",
      de: "Filterkerzen",
      fr: "Cartouches de filtration",
      pt: "Cartuchos de filtração"
    },
    href: "/FilterCartridgesPage"
  }, {
    title: {
      en: "Screen Packs",
      zh: "组合滤网",
      es: "Paquetes de mallas filtrantes",
      de: "Mehrlagen-Siebpakete",
      fr: "Packs de toiles filtrantes",
      pt: "Pacotes de telas filtrantes"
    },
    href: "/ScreenPacksPage"
  }, {
    title: {
      en: "Filter Baskets & Plates",
      zh: "滤篮与滤板",
      es: "Cestas y placas filtrantes",
      de: "Filterkörbe & Filterplatten",
      fr: "Paniers et plaques de filtration",
      pt: "Cestos e placas filtrantes"
    },
    href: "/FilterBasketsAndPlatesPage"
  }],
  href: "/FilterComponentsPage",
  linkText: {
    en: "Explore Products",
    zh: "了解产品",
    es: "Explorar productos",
    de: "Produkte erkunden",
    fr: "Explorer les produits",
    pt: "Explorar produtos"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-components-1.webp",
  alt: {
    en: "Fabricated stainless steel filter discs, mesh packs and cylindrical filter elements",
    zh: "加工不锈钢滤片、滤网组合及圆筒形过滤元件",
    es: "Discos filtrantes de acero inoxidable procesados, paquetes de mallas y elementos filtrantes cilíndricos",
    de: "Konfektionierte Edelstahl-Filterscheiben, Maschenpakete und zylindrische Filterelemente",
    fr: "Disques filtrants en acier inoxydable fabriqués, packs de toiles et éléments filtrants cylindriques",
    pt: "Discos filtrantes de aço inoxidável fabricados, pacotes de malhas e elementos filtrantes cilíndricos"
  }
}, {
  id: "wedge-wire-screens",
  index: "04",
  title: {
    en: "Wedge Wire Screens",
    zh: "楔形绕丝筛网",
    es: "Tamices de alambre en cuña",
    de: "Spaltsiebe (Keilspaltsiebe)",
    fr: "Crépines à fil profilé (wedge wire)",
    pt: "Telas de arame em cunha"
  },
  description: {
    en: "Continuous-slot V-wire screens for industrial dewatering, particle screening, and solid-liquid separation.",
    zh: "连续缝隙 V 型绕丝全焊筛网，用于工业脱水、颗粒筛分与固液分离。",
    es: "Tamices de alambre en V con ranura continua para deshidratación industrial, cribado y separación sólido-líquido.",
    de: "Spaltsiebe mit kontinuierlichem V-Draht-Profil für industrielle Entwässerung, Siebung und Fest-Flüssig-Trennung.",
    fr: "Crépines à fil profilé en V à fente continue pour l'égouttage industriel, le criblage et la séparation liquide-solide.",
    pt: "Telas de arame perfilado em V com ranhura contínua para desaguamento industrial, peneiramento e separação sólido-líquido."
  },
  specs: [{
    label: {
      en: "Slot width",
      zh: "缝隙宽度",
      es: "Ancho de ranura",
      de: "Spaltweite",
      fr: "Largeur de fente",
      pt: "Largura de ranhura"
    },
    value: "0.02–5.0",
    unit: {
      en: "mm",
      zh: "mm",
      es: "mm",
      de: "mm",
      fr: "mm",
      pt: "mm"
    }
  }, {
    label: {
      en: "Profile type",
      zh: "型材结构",
      es: "Tipo de perfil",
      de: "Profiltyp",
      fr: "Type de profil",
      pt: "Tipo de perfil"
    },
    value: "V-wire",
    unit: {
      en: "continuous",
      zh: "连续绕丝",
      es: "continuo",
      de: "endlos",
      fr: "continu",
      pt: "contínuo"
    }
  }, {
    label: {
      en: "Construction",
      zh: "制作方式",
      es: "Construcción",
      de: "Bauweise",
      fr: "Fabrication",
      pt: "Construção"
    },
    value: "All-welded",
    unit: {
      en: "rigid",
      zh: "全熔焊",
      es: "soldado",
      de: "verschweißt",
      fr: "entièrement soudé",
      pt: "todo soldado"
    }
  }],
  subProducts: [{
    title: {
      en: "Flat Panel Screens",
      zh: "平板滤网",
      es: "Paneles planos de tamiz",
      de: "Flachsiebe (Spaltsiebplatten)",
      fr: "Panneaux filtrants plats",
      pt: "Painéis de tela planos"
    },
    href: "/FlatPanelScreensPage"
  }, {
    title: {
      en: "Cylindrical Screens",
      zh: "圆筒滤网",
      es: "Tamices cilíndricos",
      de: "Spaltsiebzylinder",
      fr: "Tamis cylindriques",
      pt: "Telas cilíndricas"
    },
    href: "/CylindricalScreensPage"
  }, {
    title: {
      en: "Screen Tubes",
      zh: "筛管",
      es: "Tubos filtrantes de ranura continua",
      de: "Spaltsiebrohre",
      fr: "Tubes crépines",
      pt: "Tubos filtrantes perfilados"
    },
    href: "/ScreenTubesPage"
  }, {
    title: {
      en: "Sieve Bend Screens",
      zh: "弧形筛板",
      es: "Tamices curvos (sieve bend)",
      de: "Bogensiebe",
      fr: "Grilles courbes (sieve bend)",
      pt: "Telas curvas (sieve bend)"
    },
    href: "/SieveBendScreensPage"
  }, {
    title: {
      en: "Filter Nozzles",
      zh: "滤水帽/喷嘴",
      es: "Boquillas y toberas filtrantes",
      de: "Filterdüsen",
      fr: "Crépines de fond et buses",
      pt: "Bicos e bicos filtrantes"
    },
    href: "/FilterNozzlesPage"
  }],
  href: "/WedgeWireScreenPage",
  linkText: {
    en: "Explore Products",
    zh: "了解产品",
    es: "Explorar productos",
    de: "Produkte erkunden",
    fr: "Explorer les produits",
    pt: "Explorar produtos"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/10/wegde-wire-screen-1.webp",
  alt: {
    en: "Continuous slot V-wire stainless steel wedge wire screen panel",
    zh: "连续缝隙V型绕丝不锈钢楔形筛板",
    es: "Panel de tamiz de alambre en cuña de acero inoxidable con hilo en V y ranura continua",
    de: "Spaltsiebpaneel aus Edelstahl mit kontinuierlichem V-Draht-Spalt",
    fr: "Panneau de crépine à fil profilé en V en acier inoxydable à fente continue",
    pt: "Painel de tela de arame em cunha de aço inoxidável com perfil V de ranhura contínua"
  }
}];
const quickNavChips = [{
  href: "#woven-wire-mesh",
  label: {
    en: "Woven Wire Mesh",
    zh: "编织金属丝网",
    es: "Malla tejida",
    de: "Drahtgewebe",
    fr: "Toile tissée",
    pt: "Malha tecida"
  }
}, {
  href: "#sintered-wire-mesh",
  label: {
    en: "Sintered Wire Mesh",
    zh: "烧结金属网",
    es: "Malla sinterizada",
    de: "Sinterdrahtgewebe",
    fr: "Toile frittée",
    pt: "Malha sinterizada"
  }
}, {
  href: "#filter-components",
  label: {
    en: "Filter Components",
    zh: "深加工过滤元件",
    es: "Componentes de filtración",
    de: "Filterkomponenten",
    fr: "Composants filtrants",
    pt: "Componentes filtrantes"
  }
}, {
  href: "#wedge-wire-screens",
  label: {
    en: "Wedge Wire Screens",
    zh: "楔形绕丝筛网",
    es: "Tamices de alambre en cuña",
    de: "Spaltsiebe",
    fr: "Crépines à fil profilé",
    pt: "Telas de arame em cunha"
  }
}, {
  href: "#by-application",
  label: {
    en: "By Application",
    zh: "按应用领域",
    es: "Por aplicación",
    de: "Nach Anwendung",
    fr: "Par application",
    pt: "Por aplicação"
  }
}, {
  href: "#by-precision",
  label: {
    en: "By Precision",
    zh: "按过滤精度",
    es: "Por precisión",
    de: "Nach Feinheit",
    fr: "Par précision",
    pt: "Por precisão"
  }
}, {
  href: "#by-material",
  label: {
    en: "By Material",
    zh: "按材料材质",
    es: "Por material",
    de: "Nach Werkstoff",
    fr: "Par matériau",
    pt: "Por material"
  }
}];
export default function ProductCategories() {
  const {
    lang
  } = useLanguage();
  return <section id="productcategoriesoverview" data-nav="light" className="border-y border-border bg-background mf-section text-foreground">
      <div className="mf-container">
        {/* Header Area */}
        <Reveal>
          <div className="max-w-3xl mb-8 md:mb-10">
            <h2 className="mf-h2 text-foreground mf-heading-accent">
              {pick(lang, {
              en: "Product Categories",
              zh: "产品类别",
              es: "Categorías de productos",
              de: "Produktkategorien",
              fr: "Catégories de produits",
              pt: "Categorias de produtos"
            })}
            </h2>
            <p className="mt-4 mf-lead leading-relaxed text-muted-foreground">
              {pick(lang, {
              en: "Our range covers woven wire mesh, sintered wire mesh, filter components and wedge wire screens for industrial filtration and separation.",
              zh: "我们的产品系列涵盖编织金属丝网、烧结金属网、深加工过滤元件和楔形绕丝筛网，满足工业精密过滤与固液分离要求。",
              es: "Nuestra gama abarca malla de alambre tejida, malla sinterizada, componentes de filtración y tamices de alambre en cuña para filtración y separación industrial.",
              de: "Unser Sortiment umfasst Drahtgewebe, Sinterdrahtgewebe, Filterkomponenten und Spaltsiebe für industrielle Filtration und Trennung.",
              fr: "Notre gamme couvre les toiles métalliques tissées, toiles métalliques frittées, composants de filtration et crépines à fil profilé pour la filtration et séparation industrielle.",
              pt: "Nossa linha abrange malha de arame tecida, malha de arame sinterizada, componentes de filtração e telas de arame em cunha para filtração e separação industrial."
            })}
            </p>
          </div>
        </Reveal>

        {/* Quick Navigation Chips */}
        <Reveal delay={40}>
          <div className="mb-10 flex flex-wrap items-center gap-2">
            <span className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground mr-1">
              {pick(lang, {
              en: "Find by:",
              zh: "快速跳转:",
              es: "Buscar por:",
              de: "Finden nach:",
              fr: "Accès rapide :",
              pt: "Buscar por:"
            })}
            </span>
            {quickNavChips.map(chip => <Link key={chip.href} to={chip.href} className="inline-flex min-h-[44px] items-center rounded-full border border-border bg-muted/60 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary hover:bg-muted hover:text-primary">
                {pick(lang, chip.label)}
              </Link>)}
          </div>
        </Reveal>

        {/* 2x2 Responsive Grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          {categoriesData.map((category, index) => <Reveal key={category.id} delay={Math.min(index * 80, 300)} className="h-full">
              <Card id={category.id} className="mf-category-card mf-card-hover group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:bg-muted/30 scroll-mt-24">
                {/* Category Image - Large & prominent with zoom */}
                <div className="mf-card-media mf-ratio-card mf-img-zoom w-full relative">
                  <img src={category.image} alt={category.alt[lang] ?? category.alt.en} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>

                <div className="flex flex-1 flex-col p-6 md:p-8">
                  {/* Index Number */}
                  <div className="flex items-center justify-between pb-2">
                    <span className="mf-caption font-mono text-muted-foreground">
                      {category.index}
                    </span>
                  </div>

                  {/* Card Title with stretched link and category open cue */}
                  <h3 className="mf-h3 mf-card-title text-card-foreground">
                    <Link to={category.href} className="inline-flex items-center gap-2 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded">
                      <span className="absolute inset-0 z-0" aria-hidden="true" />
                      <span>{category.title[lang] ?? category.title.en}</span>
                      <ArrowRight className="mf-arrow-nudge size-4 shrink-0 text-muted-foreground transition-colors duration-150 group-hover:text-primary" />
                    </Link>
                  </h3>

                  {/* Card Description - shortened to one sentence */}
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {category.description[lang] ?? category.description.en}
                  </p>

                  {/* Spec Cells Grid (2-3 small cells per card) */}
                  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {category.specs.map((spec, specIdx) => <div key={specIdx} className="rounded-md border border-border bg-muted/50 p-2.5">
                        <span className="block mf-caption truncate font-medium uppercase tracking-wider text-muted-foreground">
                          {pick(lang, spec.label)}
                        </span>
                        <div className="mt-1 flex items-baseline gap-1">
                          <span className="font-mono text-base font-semibold tabular-nums text-foreground">
                            {spec.value}
                          </span>
                          <span className="mf-caption text-muted-foreground">
                            {pick(lang, spec.unit)}
                          </span>
                        </div>
                      </div>)}
                  </div>

                  {/* Sub-product Links (2-column plain text-link list, relative z-10 for independent clickability) */}
                  <div className="mt-6 border-t border-border pt-4">
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {category.subProducts.map(sub => <Link key={sub.href} to={sub.href} className="group/sub relative z-10 flex min-h-[44px] items-center justify-between py-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                          <span className="pr-2 truncate">
                            {sub.title[lang] ?? sub.title.en}
                          </span>
                          <ArrowRight className="size-3.5 shrink-0 text-muted-foreground transition-colors group-hover/sub:text-primary" />
                        </Link>)}
                    </div>
                  </div>
                </div>
              </Card>
            </Reveal>)}
        </div>
      </div>
    </section>;
}